import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { CustomDomainPlatformType, CustomDomainsApi, CustomDomainTarget } from '@va/shared/api/custom-domains';
import { useGetTargetedDNSRecords } from '@va/shared/api/custom-domains/custom-domains.api.hooks';
import { DNSVerificationStatusMap } from '@va/shared/custom-domains';
import { selectAgencyId } from '@va/standalone/shared/selectors';
import { capitalizeFirstLetter, extractDomainFromEmail } from '@va/util/helpers';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';

export enum SenderEmailCreationStep {
  verifyDNS = 'verifyDNS',
  addSenderName = 'addSenderName',
}

type SenderEmailCreationContext = ReturnType<typeof useSenderEmailCreationLogic>;

const SenderEmailCreationContext = createContext<SenderEmailCreationContext>({} as SenderEmailCreationContext);

export type SenderEmailCreationContextParams = {
  email: string;
  customSenderEmailId: number | undefined;
  closeModal: () => void;
  onSuccess?: () => void;
};

export const SenderEmailCreationContextProvider = ({
  children,
  ...rest
}: PropsWithChildren<SenderEmailCreationContextParams>) => {
  const value = useSenderEmailCreationLogic({ ...rest });
  return <SenderEmailCreationContext.Provider value={value}>{children}</SenderEmailCreationContext.Provider>;
};

const useSenderEmailCreationLogic = ({
  email,
  closeModal,
  onSuccess,
  customSenderEmailId,
}: SenderEmailCreationContextParams) => {
  const [isLoadingFinalConfirm, setIsLoadingFinalConfirm] = useState(false);
  const [dnsVerificationStatusMap, setDnsVerificationStatusMap] = useState<DNSVerificationStatusMap>({});
  const [activeStep, setActiveStep] = useState<SenderEmailCreationStep>(SenderEmailCreationStep.verifyDNS);
  const domain = useMemo(() => extractDomainFromEmail(email), [email]);

  const translate = useTranslate();
  const agencyId = useSelector(selectAgencyId);

  const dnsRecordsReq = useGetTargetedDNSRecords(
    !domain
      ? null
      : {
          platform: CustomDomainPlatformType.AWL,
          domain: domain,
          target: CustomDomainTarget.email,
        },
  );

  const senderNameForm = useFormik({
    initialValues: {
      senderName: '',
    },
    validationSchema: object().shape({
      senderName: string().required(translate('form.requiredField')),
    }),
    onSubmit: () => {},
  });

  const { showSuccessNotification, showErrorNotification } = useAddNotification();

  const listSenderEmailsReq = CustomDomainsApi.useListSenderEmails({ companyId: agencyId ?? '' });

  const onFinalConfirm = useCallback(async () => {
    if (!agencyId) return;
    setIsLoadingFinalConfirm(true);
    try {
      if (customSenderEmailId) {
        await CustomDomainsApi.updateCustomSenderName({
          companyId: agencyId,
          senderEmailId: customSenderEmailId,
          senderName: senderNameForm.values.senderName,
        });
        await CustomDomainsApi.activateSenderEmail({
          companyId: agencyId,
          senderEmailId: customSenderEmailId,
        });
        await listSenderEmailsReq.mutate();
      } else {
        await CustomDomainsApi.createSenderEmail({
          type: CustomDomainPlatformType.AWL,
          companyId: agencyId,
          email: email.trim(),
          senderName: senderNameForm.values.senderName,
        });
        await listSenderEmailsReq.mutate();
      }

      closeModal();
      onSuccess?.();
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.detail;
        if (msg) {
          showErrorNotification(undefined, capitalizeFirstLetter(msg));
        } else {
          showErrorNotification();
        }
      } else {
        showErrorNotification();
      }
    } finally {
      setIsLoadingFinalConfirm(false);
    }
  }, [
    agencyId,
    closeModal,
    customSenderEmailId,
    email,
    listSenderEmailsReq,
    onSuccess,
    senderNameForm.values.senderName,
    showErrorNotification,
    showSuccessNotification,
  ]);

  return {
    activeStep,
    senderNameForm,
    dnsRecordsReq,
    setActiveStep,
    domain,
    dnsVerificationStatusMap,
    setDnsVerificationStatusMap,
    closeModal,
    email,
    onSuccess,
    onFinalConfirm,
    isLoadingFinalConfirm,
  };
};

export const useSenderEmailCreationContext = () => useContext(SenderEmailCreationContext);
